import { useState, useEffect, useRef } from 'react'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  formatDistanceToNow,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import {
  X,
  AlignLeft,
  Calendar,
  Clock,
  Users,
  Tag,
  CircleCheck,
  MessageSquare,
  Archive,
  Plus,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Download,
} from 'lucide-react'
import { Button, ConfirmDialog } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import {
  useCardDetail,
  useUpdateCard,
  useCardComments,
  useAddComment,
  useCardChecklists,
  useCreateChecklist,
  useDeleteChecklist,
  useCreateChecklistItem,
  useUpdateChecklistItem,
  useDeleteChecklistItem,
  useCardActivities,
  useUploadCardAttachments,
  useDeleteCardAttachment,
} from '../hooks/useBoards'
import { useUsersList } from '@/features/users/hooks/useUsers'
import { cn } from '@/lib/cn'
import { getAvatarColorClass, getAvatarColorClassByIndex } from '@/components/common/Avatar'
import { useAuthStore } from '@/stores/useAuthStore'
import type { ChecklistItemData } from '@/api/services/boards.service'
import { buildChecklistTree, countChecklistLeaves } from '../utils/checklistTree'

interface CardDetailModalProps {
  boardId: string
  cardId: string | null
  onClose: () => void
}

const PRIORITY_OPTIONS = [
  { value: 'none', label: 'None', textClass: 'text-gray-600 dark:text-gray-400', badgeClass: 'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100', selectedClass: 'border border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100' },
  { value: 'low', label: 'Low', textClass: 'text-blue-600 dark:text-blue-300', badgeClass: 'border-blue-400/80 bg-blue-100 text-blue-700 dark:border-blue-400/70 dark:bg-blue-500/20 dark:text-blue-300', selectedClass: 'border border-blue-400/80 bg-blue-100 text-blue-700 dark:border-blue-400/70 dark:bg-blue-500/20 dark:text-blue-300' },
  { value: 'medium', label: 'Medium', textClass: 'text-yellow-700 dark:text-yellow-300', badgeClass: 'border-yellow-500/80 bg-yellow-100 text-yellow-700 dark:border-yellow-400/70 dark:bg-yellow-500/20 dark:text-yellow-300', selectedClass: 'border border-yellow-500/80 bg-yellow-100 text-yellow-700 dark:border-yellow-400/70 dark:bg-yellow-500/20 dark:text-yellow-300' },
  { value: 'high', label: 'High', textClass: 'text-orange-600 dark:text-orange-300', badgeClass: 'border-orange-400/80 bg-orange-100 text-orange-700 dark:border-orange-400/70 dark:bg-orange-500/20 dark:text-orange-300', selectedClass: 'border border-orange-400/80 bg-orange-100 text-orange-700 dark:border-orange-400/70 dark:bg-orange-500/20 dark:text-orange-300' },
  { value: 'urgent', label: 'Urgent', textClass: 'text-red-600 dark:text-red-300', badgeClass: 'border-red-400/80 bg-red-100 text-red-700 dark:border-red-400/70 dark:bg-red-500/20 dark:text-red-300', selectedClass: 'border border-red-400/80 bg-red-100 text-red-700 dark:border-red-400/70 dark:bg-red-500/20 dark:text-red-300' },
]

const RELATED_TO_OPTIONS = [
  { value: 'time-correction', label: 'Time corrections' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'leave', label: 'Leave requests' },
  { value: 'shift', label: 'Shifts' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'penalty', label: 'Penalties' },
  { value: 'bonus', label: 'Bonuses' },
  { value: 'company', label: 'Company' },
  { value: 'other', label: 'Other work' },
] as const

function getRelatedToLabel(entityType?: string): string | null {
  if (!entityType) return null
  const normalized = entityType.trim().toLowerCase().replace(/[_\s]/g, '-')
  const relation = RELATED_TO_OPTIONS.find((option) => option.value === normalized)
  if (relation) return relation.label
  if (normalized === 'timecorrection') return 'Time corrections'
  return entityType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function getRelatedToValue(entityType?: string): string {
  if (!entityType) return ''
  const normalized = entityType.trim().toLowerCase().replace(/[_\s]/g, '-')
  if (RELATED_TO_OPTIONS.some((option) => option.value === normalized)) return normalized
  return normalized === 'timecorrection' ? 'time-correction' : ''
}

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2)
  const minutes = (index % 2) * 30
  const date = new Date(2000, 0, 1, hours, minutes)
  return { value: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`, label: format(date, 'h:mm a') }
})

/** Format a Date/ISO-string to `YYYY-MM-DDTHH:mm` for datetime-local input */
function toDateTimeLocal(d: string | Date | undefined | null): string {
  if (!d) return ''
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function formatRelativeTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  if (Date.now() - date.getTime() < 60_000) return 'just now'
  return formatDistanceToNow(date, { addSuffix: true })
}

/** Get due date status badge info */
function getDueDateStatus(dueDate: string | Date | undefined | null, completedAt: string | Date | undefined | null) {
  if (!dueDate) return null
  if (completedAt) return { label: 'Complete', className: 'bg-green-500/15 text-green-600 dark:text-green-400' }
  const now = new Date()
  const due = new Date(dueDate)
  const diffMs = due.getTime() - now.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)
  if (diffMs < 0) return { label: 'Overdue', className: 'bg-red-500/15 text-red-600 dark:text-red-400' }
  if (diffHours <= 24) return { label: 'Due soon', className: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400' }
  return null
}

export function CardDetailModal({ boardId, cardId, onClose }: CardDetailModalProps) {
  const currentUser = useAuthStore((state) => state.user)
  const { data: card, isLoading } = useCardDetail(boardId || null, cardId)
  const { data: comments } = useCardComments(boardId || null, cardId)
  const { data: checklists } = useCardChecklists(boardId || null, cardId)
  const { data: activities } = useCardActivities(boardId || null, cardId)
  const updateCard = useUpdateCard()
  const addComment = useAddComment()
  const createChecklist = useCreateChecklist()
  const deleteChecklist = useDeleteChecklist()
  const createChecklistItem = useCreateChecklistItem()
  const updateChecklistItem = useUpdateChecklistItem()
  const deleteChecklistItem = useDeleteChecklistItem()
  const uploadAttachments = useUploadCardAttachments()
  const deleteAttachment = useDeleteCardAttachment()
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const timePickerRef = useRef<HTMLDivElement>(null)
  const assigneePickerRef = useRef<HTMLElement>(null)
  const relatedToPickerRef = useRef<HTMLDivElement>(null)

  const { data: usersData } = useUsersList({ page: 1, limit: 100 })
  const allUsers = usersData?.data ?? []

  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const [editingDesc, setEditingDesc] = useState(false)
  const [descValue, setDescValue] = useState('')
  const [commentText, setCommentText] = useState('')
  const [newChecklistTitle, setNewChecklistTitle] = useState('')
  const [addingChecklist, setAddingChecklist] = useState(false)
  const [checklistPendingDeletion, setChecklistPendingDeletion] = useState<{ _id: string; title: string } | null>(null)
  const [addingItemTo, setAddingItemTo] = useState<string | null>(null)
  const [newItemTitle, setNewItemTitle] = useState('')
  // Sub-items: which parent is taking a new sub-item, and which parents are expanded.
  const [addingSubItemTo, setAddingSubItemTo] = useState<string | null>(null)
  const [newSubItemTitle, setNewSubItemTitle] = useState('')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<'details' | 'activity'>('details')
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false)
  const [showRelatedToDropdown, setShowRelatedToDropdown] = useState(false)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [startDateValue, setStartDateValue] = useState('')
  const [dueDateValue, setDueDateValue] = useState('')
  const [calendarMonth, setCalendarMonth] = useState(() => new Date())
  const [dateField, setDateField] = useState<'start' | 'due'>('due')
  const [timePickerField, setTimePickerField] = useState<'start' | 'due' | null>(null)
  const [customTimeValue, setCustomTimeValue] = useState('')
  const [showActivityDetails, setShowActivityDetails] = useState(false)

  // Sync local date state whenever card data changes (MUST be before any early returns)
  useEffect(() => {
    if (card) {
      setStartDateValue(toDateTimeLocal(card.startDate))
      setDueDateValue(toDateTimeLocal(card.dueDate))
    }
  }, [card?.startDate, card?.dueDate])

  useEffect(() => {
    if (!timePickerField) return
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target as Node)) {
        setTimePickerField(null)
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [timePickerField])

  useEffect(() => {
    if (!showAssigneeDropdown) return
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (assigneePickerRef.current && !assigneePickerRef.current.contains(event.target as Node)) {
        setShowAssigneeDropdown(false)
      }
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick)
  }, [showAssigneeDropdown])

  useEffect(() => {
    if (!showRelatedToDropdown) return
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (relatedToPickerRef.current && !relatedToPickerRef.current.contains(event.target as Node)) {
        setShowRelatedToDropdown(false)
      }
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick)
  }, [showRelatedToDropdown])

  if (!cardId) return null

  const handleToggleAssignee = (userId: string) => {
    if (!card) return
    const currentAssigneeIds = card.assignees.map((a) => a._id)
    const newAssignees = currentAssigneeIds.includes(userId)
      ? currentAssigneeIds.filter((id) => id !== userId)
      : [...currentAssigneeIds, userId]
    const optimisticAssignees = newAssignees
      .map((id) => card.assignees.find((assignee) => assignee._id === id) ?? allUsers.find((user) => user._id === id))
      .filter((user): user is NonNullable<typeof user> => Boolean(user))
    updateCard.mutate({
      boardId,
      cardId: card._id,
      payload: { assignees: newAssignees },
      optimisticAssignees,
    })
    setShowAssigneeDropdown(false)
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="relative z-10 mx-4 w-full max-w-2xl rounded-2xl bg-white p-6 dark:bg-gray-800">
          <SectionLoader variant="detail" label="Loading card" />
        </div>
      </div>
    )
  }

  if (!card) return null

  const getDateFromValue = (value: string): Date | null => {
    if (!value) return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const setDateForField = (field: 'start' | 'due', date: Date) => {
    setDateField(field)
    const current = getDateFromValue(field === 'start' ? startDateValue : dueDateValue) ?? new Date()
    const next = new Date(date)
    next.setHours(current.getHours(), current.getMinutes(), 0, 0)
    if (field === 'start') setStartDateValue(toDateTimeLocal(next))
    else setDueDateValue(toDateTimeLocal(next))
  }

  const setTimeForField = (field: 'start' | 'due', time: string) => {
    const current = getDateFromValue(field === 'start' ? startDateValue : dueDateValue) ?? new Date()
    const [hours, minutes] = time.split(':').map(Number)
    current.setHours(hours || 0, minutes || 0, 0, 0)
    if (field === 'start') setStartDateValue(toDateTimeLocal(current))
    else setDueDateValue(toDateTimeLocal(current))
  }

  const applyCustomTime = (field: 'start' | 'due') => {
    const match = customTimeValue.trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i)
    if (!match) return
    let hours = Number(match[1])
    const minutes = Number(match[2])
    if (minutes > 59 || hours > 23) return
    const period = match[3]?.toUpperCase()
    if (period && (hours < 1 || hours > 12)) return
    if (period === 'PM' && hours !== 12) hours += 12
    if (period === 'AM' && hours === 12) hours = 0
    setTimeForField(field, `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`)
    setTimePickerField(null)
    setCustomTimeValue('')
  }

  const toggleDateField = (field: 'start' | 'due', enabled: boolean) => {
    setDateField(field)
    if (!enabled) {
      if (field === 'start') setStartDateValue('')
      else setDueDateValue('')
      return
    }
    const fallback = getDateFromValue(field === 'start' ? dueDateValue : startDateValue) ?? new Date()
    setDateForField(field, fallback)
  }

  const openDatePicker = () => {
    const initialDate = getDateFromValue(dueDateValue) ?? getDateFromValue(startDateValue) ?? new Date()
    setCalendarMonth(initialDate)
    setDateField(dueDateValue ? 'due' : 'start')
    setShowDatePicker(true)
  }

  const cancelDatePicker = () => {
    setStartDateValue(toDateTimeLocal(card.startDate))
    setDueDateValue(toDateTimeLocal(card.dueDate))
    setShowDatePicker(false)
  }

  const calendarDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(calendarMonth)),
    end: endOfWeek(endOfMonth(calendarMonth)),
  })

  const selectedDate = getDateFromValue(dateField === 'start' ? startDateValue : dueDateValue)

  const handleTitleSave = () => {
    if (titleValue.trim() && titleValue !== card.title) {
      updateCard.mutate({ boardId, cardId: card._id, payload: { title: titleValue.trim() } })
    }
    setEditingTitle(false)
  }

  const handleDescSave = () => {
    if (descValue !== card.description) {
      updateCard.mutate({ boardId, cardId: card._id, payload: { description: descValue } })
    }
    setEditingDesc(false)
  }

  const handlePriorityChange = (priority: string) => {
    updateCard.mutate({ boardId, cardId: card._id, payload: { priority } })
  }

  const handleRelatedToChange = (entityType: string) => {
    setShowRelatedToDropdown(false)
    if (entityType === getRelatedToValue(card.entityType)) return
    updateCard.mutate({ boardId, cardId: card._id, payload: { entityType } })
  }

  const handleAttachmentSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length > 0) uploadAttachments.mutate({ boardId, cardId: card._id, files })
    event.target.value = ''
  }

  const handleAddComment = () => {
    if (!commentText.trim()) return
    addComment.mutate({ boardId, cardId: card._id, content: commentText.trim() })
    setCommentText('')
  }

  const handleAddChecklist = () => {
    if (!newChecklistTitle.trim()) return
    const title = newChecklistTitle.trim()
    setNewChecklistTitle('')
    setAddingChecklist(false)
    createChecklist.mutate(
      { boardId, cardId: card._id, title },
      { onSuccess: (checklist) => setAddingItemTo(checklist._id) },
    )
  }

  const handleAddChecklistItem = (checklistId: string) => {
    if (!newItemTitle.trim()) return
    createChecklistItem.mutate({ boardId, cardId: card._id, checklistId, title: newItemTitle.trim() })
    setNewItemTitle('')
    // Keep addingItemTo open for adding subsequent items continuously
  }

  const handleAddSubItem = (checklistId: string, parentItemId: string) => {
    if (!newSubItemTitle.trim()) return
    createChecklistItem.mutate({ boardId, cardId: card._id, checklistId, title: newSubItemTitle.trim(), parentItemId })
    setNewSubItemTitle('')
    setExpandedItems((current) => new Set(current).add(parentItemId))
  }

  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems((current) => {
      const next = new Set(current)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  const handleToggleChecklistItem = (checklistId: string, itemId: string, isCompleted: boolean) => {
    updateChecklistItem.mutate({
      boardId, cardId: card._id, checklistId, itemId,
      payload: { isCompleted: !isCompleted },
    })
  }

  const handleDeleteChecklistItem = (checklistId: string, itemId: string) => {
    deleteChecklistItem.mutate({ boardId, cardId: card._id, checklistId, itemId })
  }

  const handleDelete = () => {
    updateCard.mutate({ boardId, cardId: card._id, payload: { isArchived: true } })
    onClose()
  }

  const handleDateSave = () => {
    const payload: { startDate?: string | null; dueDate?: string | null } = {}
    const newStart = startDateValue ? new Date(startDateValue).toISOString() : null
    const newDue = dueDateValue ? new Date(dueDateValue).toISOString() : null
    const oldStart = card.startDate ? new Date(card.startDate).toISOString() : null
    const oldDue = card.dueDate ? new Date(card.dueDate).toISOString() : null
    if (newStart !== oldStart) payload.startDate = newStart
    if (newDue !== oldDue) payload.dueDate = newDue
    if (Object.keys(payload).length > 0) {
      updateCard.mutate({ boardId, cardId: card._id, payload })
    }
    setShowDatePicker(false)
  }

  const handleRemoveDates = () => {
    setStartDateValue('')
    setDueDateValue('')
    updateCard.mutate({
      boardId,
      cardId: card._id,
      payload: { startDate: null, dueDate: null },
    })
    setShowDatePicker(false)
  }

  const dueDateStatus = getDueDateStatus(card.dueDate, card.completedAt)

  const createdByName = typeof card.createdBy === 'object' && card.createdBy
    ? `${card.createdBy.firstName} ${card.createdBy.lastName}`
    : 'Unknown'

  const formatActivityType = (type: string) => {
    const labels: Record<string, string> = {
      created: 'created this card',
      updated: 'updated',
      moved: 'moved this card',
      assigned: 'assigned',
      unassigned: 'unassigned',
      commented: 'commented',
      label_added: 'added a label',
      label_removed: 'removed a label',
      checklist_added: 'added a checklist',
      checklist_removed: 'removed a checklist',
      archived: 'archived this card',
    }
    return labels[type] || type
  }

  const formatActivityDetails = (activity: { type: string; details: Record<string, unknown> }) => {
    if (activity.type === 'moved') {
      const fromList = typeof activity.details?.fromList === 'string' ? activity.details.fromList : 'a list'
      const toList = typeof activity.details?.toList === 'string' ? activity.details.toList : 'another list'
      return `moved this card from ${fromList} to ${toList}`
    }
    if (activity.type === 'checklist_added') {
      const checklistTitle = typeof activity.details?.checklistTitle === 'string' ? activity.details.checklistTitle : 'a checklist'
      return `added ${checklistTitle} to this card`
    }
    if (activity.type === 'checklist_removed') {
      const checklistTitle = typeof activity.details?.checklistTitle === 'string' ? activity.details.checklistTitle : 'a checklist'
      return `removed ${checklistTitle} from this card`
    }
    if (activity.type.startsWith('checklist_item_')) {
      const itemTitle = typeof activity.details?.itemTitle === 'string' ? activity.details.itemTitle : 'a checklist item'
      const checklistTitle = typeof activity.details?.checklistTitle === 'string' ? activity.details.checklistTitle : 'the checklist'
      const messages: Record<string, string> = {
        checklist_item_added: `added ${itemTitle} to ${checklistTitle}`,
        checklist_item_completed: `completed ${itemTitle} on this card`,
        checklist_item_reopened: `marked ${itemTitle} incomplete on this card`,
        checklist_item_updated: `updated ${itemTitle}`,
        checklist_item_removed: `removed ${itemTitle} from ${checklistTitle}`,
      }
      return messages[activity.type] ?? formatActivityType(activity.type)
    }
    if (activity.type !== 'updated') return formatActivityType(activity.type)
    const field = String(activity.details?.field ?? '')
    const labels: Record<string, string> = {
      title: 'renamed this card', description: 'updated the description', priority: 'changed the priority',
      startDate: 'updated the start date', dueDate: 'updated the due date', assignees: 'updated the members',
      labels: 'updated the labels', attachments: 'updated attachments', estimate: 'updated the estimate',
    }
    if (field === 'attachments' && typeof activity.details?.newValue === 'object' && activity.details.newValue) {
      const attachment = activity.details.newValue as { removed?: boolean; count?: number; names?: string[] }
      if (attachment.removed) {
        const removedName = typeof activity.details?.oldValue === 'object' && activity.details.oldValue
          ? (activity.details.oldValue as { name?: string }).name
          : undefined
        return removedName ? `removed ${removedName} from this card` : 'removed an attachment from this card'
      }
      if (attachment.names?.length) {
        const name = attachment.names.length === 1 ? attachment.names[0] : `${attachment.names[0]} and ${attachment.names.length - 1} more`
        return `attached ${name} to this card`
      }
      return `attached ${attachment.count ?? 1} file${attachment.count === 1 ? '' : 's'} to this card`
    }
    if (field === 'dueDate' || field === 'startDate') {
      const value = typeof activity.details?.newValue === 'string' ? activity.details.newValue : ''
      if (value) {
        const date = new Date(value)
        if (!Number.isNaN(date.getTime())) {
          const oldValue = typeof activity.details?.oldValue === 'string' ? activity.details.oldValue : ''
          const oldDate = oldValue ? new Date(oldValue) : null
          const oldLabel = oldDate && !Number.isNaN(oldDate.getTime()) ? format(oldDate, "MMM d 'at' h:mm a") : ''
          const newLabel = format(date, "MMM d 'at' h:mm a")
          return oldLabel
            ? `changed the ${field === 'dueDate' ? 'due' : 'start'} date of this card from ${oldLabel} to ${newLabel}`
            : `changed the ${field === 'dueDate' ? 'due' : 'start'} date of this card to ${newLabel}`
        }
      }
    }
    return labels[field] ?? 'updated this card'
  }

  const formatActivityValue = (value: unknown, field?: string): string => {
    if (value === null || value === undefined || value === '') return 'none'
    if ((field === 'startDate' || field === 'dueDate') && typeof value === 'string') {
      const date = new Date(value)
      if (!Number.isNaN(date.getTime())) return format(date, "MMM d 'at' h:mm a")
    }
    if (typeof value === 'string') {
      const normalized = value.replace(/\s+/g, ' ').trim()
      return normalized.length > 80 ? `${normalized.slice(0, 77)}…` : normalized
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) return value.length ? `${value.length} item${value.length === 1 ? '' : 's'}` : 'none'
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>
      if (typeof record.title === 'string') return record.title
      if (typeof record.name === 'string') return record.name
      if (typeof record.firstName === 'string') return `${record.firstName} ${typeof record.lastName === 'string' ? record.lastName : ''}`.trim()
      return 'updated'
    }
    return String(value)
  }

  const formatActivityChange = (activity: { type: string; details: Record<string, unknown> }) => {
    if (activity.type !== 'updated') return ''
    const field = String(activity.details?.field ?? '')
    // Descriptions can contain a lot of text; the activity timeline should
    // stay compact and only report that the description was updated.
    if (!field || field === 'description' || field === 'attachments' || field === 'startDate' || field === 'dueDate') return ''
    if (activity.details?.oldValue === undefined && activity.details?.newValue === undefined) return ''
    return `from ${formatActivityValue(activity.details?.oldValue, field)} to ${formatActivityValue(activity.details?.newValue, field)}`
  }

  const formatActivityTimestamp = (value: string) => {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : format(date, 'MMM d, yyyy, h:mm a')
  }

  const getActorName = (
    actor?: { _id?: string; firstName?: string; lastName?: string } | string | null,
    fallbackToCurrentUser = false,
  ) => {
    if (actor && typeof actor === 'object' && (actor.firstName || actor.lastName)) {
      return `${actor.firstName ?? ''} ${actor.lastName ?? ''}`.trim()
    }
    if (typeof actor === 'string' && currentUser?.id === actor) {
      return `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() || currentUser.name || currentUser.email
    }
    if (fallbackToCurrentUser && currentUser) {
      return `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() || currentUser.name || currentUser.email
    }
    return 'Unknown'
  }

  const getActivityImages = (activity: { type: string; details: Record<string, unknown> }) => {
    const attachmentData = activity.details?.newValue as { removed?: boolean; attachments?: Array<{ name: string; url: string; mimeType: string }>; names?: string[] } | undefined
    if (activity.type !== 'updated' || activity.details?.field !== 'attachments') return []
    if (attachmentData?.removed) {
      const removedAttachment = activity.details?.oldValue as { name?: string; url?: string; mimeType?: string } | undefined
      return removedAttachment?.url && removedAttachment.mimeType?.startsWith('image/')
        ? [{ name: removedAttachment.name ?? 'Removed image', url: removedAttachment.url, mimeType: removedAttachment.mimeType }]
        : []
    }
    const storedImages = attachmentData?.attachments?.filter((attachment) => attachment.mimeType.startsWith('image/')) ?? []
    if (storedImages.length > 0) return storedImages
    return (card.attachments ?? []).filter((attachment) =>
      attachment.mimeType.startsWith('image/') && (!attachmentData?.names?.length || attachmentData.names.includes(attachment.name)),
    )
  }

  const mainActivities = (activities ?? []).filter((activity) => !activity.type.startsWith('checklist_item_'))

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
      />

      <div className="relative z-10 w-full max-w-6xl rounded-2xl border border-white/50 bg-white shadow-2xl [&_button]:cursor-pointer dark:border-gray-700 dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-100 p-5 dark:border-gray-700">
          <div className="flex-1 min-w-0">
            {/* Labels row */}
            {card.labels.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {card.labels.map((label) => (
                  <span
                    key={label._id}
                    className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white"
                    style={{ backgroundColor: label.color }}
                  >
                    {label.name}
                  </span>
                ))}
              </div>
            )}

            {editingTitle ? (
              <input
                autoFocus
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') setEditingTitle(false) }}
                className="w-full rounded-md border border-primary/30 bg-transparent px-2 py-1 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary dark:text-gray-100"
              />
            ) : (
              <h2
                className="cursor-pointer text-lg font-semibold text-gray-900 hover:text-primary dark:text-gray-100"
                onClick={() => { setEditingTitle(true); setTitleValue(card.title) }}
              >
                {card.title}
              </h2>
            )}

            {/* Priority + assignees strip */}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              {card.priority !== 'none' && (
                <span className={cn('inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide', PRIORITY_OPTIONS.find(p => p.value === card.priority)?.badgeClass)}>
                  {card.priority}
                </span>
              )}
              {getRelatedToLabel(card.entityType) && (
                <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {getRelatedToLabel(card.entityType)}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" />
                {card.assignees.length} assigned
              </span>
              {card.startDate && (
                <span className="inline-flex items-center gap-1 font-medium text-gray-600 dark:text-gray-400">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  Start: {new Date(card.startDate).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {card.dueDate && (
                <span className={cn(
                  'inline-flex items-center gap-1 font-semibold rounded px-1.5 py-0.5 text-[11px]',
                  dueDateStatus?.label === 'Overdue'
                    ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                    : dueDateStatus?.label === 'Due soon'
                      ? 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
                )}>
                  <Clock className="h-3 w-3" />
                  Due: {new Date(card.dueDate).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {dueDateStatus && ` (${dueDateStatus.label})`}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="ml-4 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="hidden border-b border-gray-100 px-5 dark:border-gray-700">
          <div className="flex gap-1">
            {(['details', 'activity'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'border-b-2 px-3 py-2.5 text-sm font-medium capitalize transition-colors',
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
                )}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="grid max-h-[68vh] min-h-0 grid-cols-1 items-start overflow-hidden lg:relative lg:block">
          {activeTab === 'details' && (
            <div className="max-h-[68vh] w-full space-y-6 overflow-y-auto thin-scroll p-5 lg:w-[62.8%]">
              {/* Description */}
              <section>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <AlignLeft className="h-4 w-4" />
                  Description
                </div>
                {editingDesc ? (
                  <div>
                    <textarea
                      autoFocus
                      value={descValue}
                      onChange={(e) => setDescValue(e.target.value)}
                      rows={4}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Add a description…"
                    />
                    <div className="mt-2 flex gap-2">
                      <Button type="button" variant="primary" onClick={handleDescSave} className="text-xs">Save</Button>
                      <Button type="button" variant="secondary" onClick={() => setEditingDesc(false)} className="text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => { setEditingDesc(true); setDescValue(card.description) }}
                    className="min-h-[96px] cursor-pointer rounded-lg border border-gray-300 bg-gray-50 px-3 py-3 text-sm text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                  >
                    {card.description || 'Add a more detailed description…'}
                  </div>
                )}
              </section>

              {/* Work category */}
              <section>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <Tag className="h-4 w-4" />
                  Work category
                </div>
                <div ref={relatedToPickerRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setShowRelatedToDropdown((isOpen) => !isOpen)}
                    className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 text-left text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/15 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-500"
                    aria-label="Work category"
                    aria-expanded={showRelatedToDropdown}
                    aria-haspopup="listbox"
                  >
                    <span>{getRelatedToLabel(card.entityType) ?? 'Select a category'}</span>
                    <ChevronDown className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform duration-150', showRelatedToDropdown && 'rotate-180')} />
                  </button>

                  {showRelatedToDropdown && (
                    <div
                      role="listbox"
                      aria-label="Work category options"
                      className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl thin-scroll dark:border-gray-600 dark:bg-gray-800"
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected={!getRelatedToValue(card.entityType)}
                        onClick={() => handleRelatedToChange('')}
                        className={cn(
                          'flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors',
                          !getRelatedToValue(card.entityType)
                            ? 'bg-primary/10 font-semibold text-primary'
                            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
                        )}
                      >
                        Select a category
                      </button>
                      {RELATED_TO_OPTIONS.map((option) => {
                        const isSelected = getRelatedToValue(card.entityType) === option.value
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => handleRelatedToChange(option.value)}
                            className={cn(
                              'flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors',
                              isSelected
                                ? 'bg-primary/10 font-semibold text-primary'
                                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
                            )}
                          >
                            {option.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </section>

              {/* Priority Selector */}
              <section>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <Tag className="h-4 w-4" />
                  Priority
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handlePriorityChange(opt.value)}
                      className={cn(
                        'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                        card.priority === opt.value
                          ? opt.selectedClass
                          : cn(opt.textClass, 'hover:bg-gray-100 dark:hover:bg-gray-700'),
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* Dates / Deadline */}
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span>Dates</span>
                    {dueDateStatus && (
                      <span className={cn('ml-1 rounded-md px-2 py-0.5 text-[10px] font-semibold', dueDateStatus.className)}>
                        {dueDateStatus.label}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={openDatePicker}
                    className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    <span>{card.dueDate || card.startDate ? 'Edit Dates' : 'Set Dates'}</span>
                  </button>
                </div>

                {/* Current dates display */}
                {(card.startDate || card.dueDate) && !showDatePicker && (
                  <div className="flex flex-wrap gap-3">
                    {card.startDate && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                        <div>
                          <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Start</div>
                          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            {new Date(card.startDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    )}
                    {card.dueDate && (
                      <div className={cn(
                        'flex items-center gap-2 rounded-lg border px-3 py-2',
                        dueDateStatus?.label === 'Overdue'
                          ? 'border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-900/20'
                          : dueDateStatus?.label === 'Due soon'
                            ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-900/40 dark:bg-yellow-900/20'
                            : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800',
                      )}>
                        <Clock className={cn(
                          'h-3.5 w-3.5',
                          dueDateStatus?.label === 'Overdue' ? 'text-red-500' : dueDateStatus?.label === 'Due soon' ? 'text-yellow-500' : 'text-gray-400',
                        )} />
                        <div>
                          <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Due</div>
                          <div className={cn(
                            'text-xs font-semibold',
                            dueDateStatus?.label === 'Overdue' ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
                          )}>
                            {new Date(card.dueDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!card.startDate && !card.dueDate && !showDatePicker && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No dates set for this card.</p>
                )}

              </section>

              {/* Attachments */}
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <Paperclip className="h-4 w-4" />
                    Attachments
                    {card.attachments?.length ? <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-700">{card.attachments.length}</span> : null}
                  </div>
                  <button type="button" onClick={() => attachmentInputRef.current?.click()} disabled={uploadAttachments.isPending} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                    <Plus className="h-3.5 w-3.5" />
                    {uploadAttachments.isPending ? 'Uploading…' : 'Add file'}
                  </button>
                  <input ref={attachmentInputRef} type="file" multiple onChange={handleAttachmentSelect} className="hidden" />
                </div>
                {card.attachments?.length ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {card.attachments.map((attachment) => {
                      const isImage = attachment.mimeType.startsWith('image/')
                      return (
                        <div key={attachment._id} className="group flex min-w-0 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
                          {isImage ? <img src={attachment.url} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200 text-gray-500 dark:bg-gray-700"><FileText className="h-5 w-5" /></div>}
                          <div className="min-w-0 flex-1">
                            <a href={attachment.url} target="_blank" rel="noreferrer" className="block truncate text-xs font-semibold text-gray-700 hover:text-primary dark:text-gray-200">{attachment.name}</a>
                            <span className="text-[10px] text-gray-400">{Math.max(1, Math.round(attachment.size / 1024))} KB</span>
                          </div>
                          <a href={attachment.url} target="_blank" rel="noreferrer" className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700"><Download className="h-3.5 w-3.5" /></a>
                          <button type="button" onClick={() => deleteAttachment.mutate({ boardId, cardId: card._id, attachmentId: attachment._id })} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20" aria-label={`Remove ${attachment.name}`}><X className="h-3.5 w-3.5" /></button>
                        </div>
                      )
                    })}
                  </div>
                ) : <div className="flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400"><ImageIcon className="h-4 w-4" /> No attachments yet.</div>}
              </section>

              {/* Members / Assignees */}
              <section ref={assigneePickerRef}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <Users className="h-4 w-4 text-primary" />
                    <span>Members / Assignees</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                    className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>{card.assignees.length > 0 ? 'Edit Members' : 'Assign Member'}</span>
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {card.assignees.map((u, index) => (
                    <div
                      key={u._id}
                      className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                    >
                      <div className={cn('flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white', getAvatarColorClassByIndex(index))}>
                        {u.firstName?.[0]}{u.lastName?.[0]}
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {u.firstName} {u.lastName}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleToggleAssignee(u._id)}
                        className="ml-1 rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-red-900/30"
                        title="Unassign member"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}

                  {card.assignees.length === 0 && !showAssigneeDropdown && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic">No members assigned to this card yet.</p>
                  )}
                </div>

                {/* Member selection dropdown */}
                {showAssigneeDropdown && (
                  <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <div className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                      Select a team member to assign:
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1 thin-scroll">
                      {allUsers.map((u, index) => {
                        const isAssigned = card.assignees.some((a) => a._id === u._id)
                        return (
                          <button
                            key={u._id}
                            type="button"
                            onClick={() => handleToggleAssignee(u._id)}
                            className={cn(
                              'flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors text-left',
                              isAssigned
                                ? 'bg-primary/10 text-primary font-semibold dark:bg-primary/20'
                                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div className={cn('flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold text-white', getAvatarColorClassByIndex(index))}>
                                {u.firstName?.[0]}{u.lastName?.[0]}
                              </div>
                              <span>{u.firstName} {u.lastName}</span>
                            </div>
                            {isAssigned && <Check className="h-3.5 w-3.5 text-primary" />}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </section>

              {/* Checklists */}
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    <CircleCheck className="h-4 w-4" />
                    Checklists
                  </div>
                  {!addingChecklist && (
                    <button
                      type="button"
                      onClick={() => setAddingChecklist(true)}
                      className={cn(
                        'inline-flex items-center justify-center rounded-md text-xs font-medium transition-colors',
                        checklists?.length
                          ? 'h-7 w-7 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200'
                          : 'gap-1 bg-gray-100 px-2.5 py-1 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
                      )}
                      title="Add another checklist"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {!checklists?.length && <span>Add</span>}
                    </button>
                  )}
                </div>

                {addingChecklist && (
                  <div className="mb-3 flex items-center gap-2">
                    <input
                      autoFocus
                      value={newChecklistTitle}
                      onChange={(e) => setNewChecklistTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddChecklist(); if (e.key === 'Escape') setAddingChecklist(false) }}
                      placeholder="Checklist name"
                      className="flex-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <Button type="button" variant="primary" onClick={handleAddChecklist} className="text-xs px-2.5 py-1">Add</Button>
                  </div>
                )}

                {checklists?.map((cl: any) => {
                  const items: ChecklistItemData[] = cl.items ?? []
                  const tree = buildChecklistTree(items)
                  // Progress counts leaf items, so a half-built group moves the bar.
                  const { total, completed } = countChecklistLeaves(items)
                  const pct = total > 0 ? Math.round((completed / total) * 100) : 0

                  const renderItemRow = (item: ChecklistItemData, children: ChecklistItemData[] | null) => {
                    const isParent = !!children && children.length > 0
                    const doneChildren = children?.filter((child) => child.isCompleted).length ?? 0
                    const isPartial = isParent && !item.isCompleted && doneChildren > 0
                    const expanded = expandedItems.has(item._id)
                    return (
                      <div className="group flex items-center justify-between gap-1 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-gray-100/80 dark:hover:bg-gray-700/50">
                        {children !== null && (
                          isParent ? (
                            <button
                              type="button"
                              onClick={() => toggleItemExpanded(item._id)}
                              className="rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.title}`}
                              aria-expanded={expanded}
                            >
                              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          ) : (
                            <span className="w-4.5 shrink-0" aria-hidden />
                          )
                        )}
                        <label className="flex flex-1 cursor-pointer items-center gap-2.5">
                          <span className={cn(
                            'relative flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-transparent transition-colors duration-150',
                            item.isCompleted
                              ? 'border-primary bg-primary text-white'
                              : isPartial
                                ? 'border-primary text-transparent'
                                : 'border-gray-400 text-transparent hover:border-gray-600 dark:border-gray-500 dark:hover:border-gray-300',
                          )}>
                            <input
                              type="checkbox"
                              checked={item.isCompleted}
                              onChange={() => handleToggleChecklistItem(cl._id, item._id, item.isCompleted)}
                              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                              aria-label={`Mark ${item.title} ${item.isCompleted ? 'incomplete' : 'complete'}`}
                            />
                            {item.isCompleted && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                            {isPartial && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />}
                          </span>
                          <span className={cn('flex-1 text-sm text-gray-800 dark:text-gray-200 transition-all', item.isCompleted && 'line-through text-gray-400 dark:text-gray-500')}>
                            {item.title}
                          </span>
                          {isParent && (
                            <span
                              className={cn(
                                'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                                item.isCompleted
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                              )}
                              title={`${doneChildren} of ${children.length} sub-items done`}
                            >
                              {doneChildren}/{children.length}
                            </span>
                          )}
                        </label>
                        {children !== null && (
                          <button
                            type="button"
                            onClick={() => { setAddingSubItemTo(item._id); setNewSubItemTitle('') }}
                            className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-700 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-600 dark:hover:text-gray-200"
                            title="Add sub-item"
                            aria-label={`Add sub-item to ${item.title}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteChecklistItem(cl._id, item._id)}
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                          title={isParent ? 'Delete item and its sub-items' : 'Delete item'}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )
                  }

                  return (
                    <div key={cl._id} className="mb-4">
                      <div className="mb-1.5 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{cl.title}</h4>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-medium text-gray-500">{pct}%</span>
                          <button
                            type="button"
                            onClick={() => setChecklistPendingDeletion({ _id: cl._id, title: cl.title })}
                            className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                            title={`Delete ${cl.title}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mb-2 h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={cn('h-full rounded-full transition-all duration-300', pct === 100 ? 'bg-green-500' : 'bg-primary')}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      {/* Items */}
                      <div className="space-y-1">
                        {tree.map(({ item, children }) => (
                          <div key={item._id}>
                            {renderItemRow(item, children)}
                            {children.length > 0 && expandedItems.has(item._id) && (
                              <div className="ml-6 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-700">
                                {children.map((child) => (
                                  <div key={child._id}>{renderItemRow(child, null)}</div>
                                ))}
                              </div>
                            )}
                            {addingSubItemTo === item._id && (
                              <div className="ml-6 mt-1 flex items-center gap-2 border-l border-gray-200 pl-2 dark:border-gray-700">
                                <input
                                  autoFocus
                                  value={newSubItemTitle}
                                  onChange={(e) => setNewSubItemTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault()
                                      handleAddSubItem(cl._id, item._id)
                                    }
                                    if (e.key === 'Escape') setAddingSubItemTo(null)
                                  }}
                                  placeholder="Add a sub-item…"
                                  className="flex-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-900 shadow-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                                <Button type="button" variant="primary" onClick={() => handleAddSubItem(cl._id, item._id)} className="text-xs px-3 py-1">
                                  Add
                                </Button>
                                <button
                                  type="button"
                                  onClick={() => setAddingSubItemTo(null)}
                                  className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  aria-label="Cancel adding sub-item"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Add item */}
                      {addingItemTo === cl._id ? (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            autoFocus
                            value={newItemTitle}
                            onChange={(e) => setNewItemTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleAddChecklistItem(cl._id)
                              }
                              if (e.key === 'Escape') setAddingItemTo(null)
                            }}
                            placeholder="Add an item…"
                            className="flex-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                          <Button type="button" variant="primary" onClick={() => handleAddChecklistItem(cl._id)} className="text-xs px-3 py-1.5">
                            Add
                          </Button>
                          <button
                            type="button"
                            onClick={() => setAddingItemTo(null)}
                            className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setAddingItemTo(cl._id); setNewItemTitle('') }}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          <span>Add an item</span>
                        </button>
                      )}
                    </div>
                  )
                })}
              </section>

              {/* Comments */}
              <section className="hidden">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <MessageSquare className="h-4 w-4" />
                  Comments
                </div>
                <div className="mb-3 flex gap-2">
                  <input
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment() }}
                    placeholder="Write a comment…"
                    className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-500"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleAddComment}
                    disabled={!commentText.trim()} loading={addComment.isPending}
                    className="text-xs"
                  >
                    Send
                  </Button>
                </div>
                <div className="space-y-3">
                  {comments?.map((c) => (
                    <div key={c._id} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
                      <div className="mb-1 flex items-center gap-2">
                        <div className={cn('flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white', getAvatarColorClass(c.authorId?._id ?? 'user'))}>
                          {c.authorId?.firstName?.[0]}{c.authorId?.lastName?.[0]}
                        </div>
                        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                          {c.authorId?.firstName} {c.authorId?.lastName}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {new Date(c.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{c.content}</p>
                    </div>
                  ))}
                  {(!comments || comments.length === 0) && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">No comments yet.</p>
                  )}
                </div>
              </section>
            </div>
          )}

          <aside className="min-h-0 max-h-[68vh] overflow-y-auto thin-scroll border-t border-gray-200 bg-gray-50/70 p-5 dark:border-gray-700 dark:bg-gray-900/25 lg:absolute lg:inset-y-0 lg:right-0 lg:w-[37.2%] lg:border-l lg:border-t-0">
            {!showActivityDetails ? (
              <>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                    <MessageSquare className="h-4 w-4" />
                    Comments and activity
                  </div>
                  <button type="button" onClick={() => setShowActivityDetails(true)} className="shrink-0 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/10">
                    Show details
                  </button>
                </div>
                <form className="mb-5" onSubmit={(event) => { event.preventDefault(); handleAddComment() }}>
                  <input
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                    placeholder="Write a comment…"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </form>
                <div className="space-y-4">
                  {comments?.map((comment) => (
                    <div key={comment._id} className="flex items-start gap-2.5">
                      <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white', getAvatarColorClass(comment.authorId?._id ?? 'user'))}>
                        {comment.authorId?.firstName?.[0]}{comment.authorId?.lastName?.[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
                          <span className="font-semibold text-gray-800 dark:text-gray-200">{getActorName(comment.authorId)}</span>
                          <time className="text-primary underline decoration-primary/40 underline-offset-2" dateTime={comment.createdAt}>{formatActivityTimestamp(comment.createdAt)}</time>
                        </div>
                        <div className="mt-1 rounded-lg bg-gray-100 px-3 py-2.5 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                          <p className="whitespace-pre-wrap">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {mainActivities.map((activity) => (
                    <div key={activity._id} className="flex items-start gap-2.5">
                      <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white', getAvatarColorClass(activity.actorId?._id ?? 'user'))}>
                        {activity.actorId?.firstName?.[0]}{activity.actorId?.lastName?.[0]}
                      </div>
                      <div className="min-w-0 pt-0.5 text-xs">
                        <p className="text-gray-600 dark:text-gray-300"><span className="font-semibold text-gray-800 dark:text-gray-200">{getActorName(activity.actorId, activity.type === 'commented')}</span> {formatActivityDetails(activity)}{formatActivityChange(activity) && <span className="text-gray-500 dark:text-gray-400">{' '}{formatActivityChange(activity)}</span>}</p>
                        <time className="mt-0.5 block text-primary underline decoration-primary/40 underline-offset-2" dateTime={activity.createdAt}>{formatActivityTimestamp(activity.createdAt)}</time>
                      </div>
                    </div>
                  ))}
                  {(!comments || comments.length === 0) && mainActivities.length === 0 && <p className="text-center text-xs text-gray-400">No comments or activity yet.</p>}
                </div>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                    <MessageSquare className="h-4 w-4" />
                    Activity details
                  </div>
                  <button type="button" onClick={() => setShowActivityDetails(false)} className="shrink-0 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/10">
                    Hide details
                  </button>
                </div>
                <div className="space-y-5 pr-1">
                  {(activities ?? []).map((activity) => {
                    const imageAttachments = getActivityImages(activity)
                    return (
                      <div key={activity._id} className="flex items-start gap-2.5">
                        <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white', getAvatarColorClass(activity.actorId?._id ?? 'user'))}>
                          {activity.actorId?.firstName?.[0]}{activity.actorId?.lastName?.[0]}
                        </div>
                        <div className="min-w-0 flex-1 text-xs">
                          <p className="text-gray-700 dark:text-gray-200"><span className="font-semibold">{getActorName(activity.actorId, activity.type === 'commented')}</span> {formatActivityDetails(activity)}{formatActivityChange(activity) && <span className="text-gray-500 dark:text-gray-400">{' '}{formatActivityChange(activity)}</span>}</p>
                          <time className="mt-0.5 block text-primary underline decoration-primary/40 underline-offset-2" dateTime={activity.createdAt}>{formatRelativeTime(activity.createdAt)}</time>
                          {imageAttachments.map((attachment) => (
                            <a key={attachment.url} href={attachment.url} target="_blank" rel="noreferrer" className="mt-3 block overflow-hidden rounded border border-gray-300 dark:border-gray-600" title={attachment.name}>
                              <img src={attachment.url} alt={attachment.name} className="max-h-52 w-full object-cover" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                  {(!activities || activities.length === 0) && <p className="text-center text-xs text-gray-400">No activity yet.</p>}
                </div>
              </>
            )}
          </aside>

          {activeTab === 'activity' && (
            <div className="space-y-3">
              {activities?.map((a) => (
                <div key={a._id} className="flex items-start gap-2.5">
                  <div className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white', getAvatarColorClass(a.actorId?._id ?? 'user'))}>
                    {a.actorId?.firstName?.[0]}{a.actorId?.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">{getActorName(a.actorId, a.type === 'commented')}</span>
                      {' '}{formatActivityDetails(a)}{formatActivityChange(a) && <span className="text-gray-500">{' '}{formatActivityChange(a)}</span>}
                      {Boolean(a.details?.field) && (
                        <span className="text-gray-500"> — {String(a.details.field)}</span>
                      )}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {new Date(a.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {(!activities || activities.length === 0) && (
                <p className="text-center text-xs text-gray-400 dark:text-gray-500">No activity yet.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 dark:border-gray-700">
          <span className="text-[10px] text-gray-400">
            Created by {createdByName} · {new Date(card.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Archive className="h-3.5 w-3.5" />
            Archive
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={!!checklistPendingDeletion}
        onClose={() => setChecklistPendingDeletion(null)}
        onConfirm={() => {
          if (!checklistPendingDeletion) return
          deleteChecklist.mutate(
            { boardId, cardId: card._id, checklistId: checklistPendingDeletion._id },
            { onSuccess: () => setChecklistPendingDeletion(null) },
          )
        }}
        title="Delete checklist?"
        message={`Delete “${checklistPendingDeletion?.title ?? 'this checklist'}” and all of its items?`}
        confirmLabel="Delete checklist"
        variant="danger"
        confirmLoading={deleteChecklist.isPending}
      />

      {showDatePicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 p-4 backdrop-blur-[1px]" onMouseDown={cancelDatePicker}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Set card dates"
            className="w-full max-w-[360px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-[#47494e] dark:bg-[#292b2f]"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-[#47494e]">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Dates</h3>
              <button type="button" onClick={cancelDatePicker} className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white" aria-label="Close date picker">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-3.5">
              <div className="mb-3 flex items-center justify-between">
                <button type="button" onClick={() => setCalendarMonth((month) => subMonths(month, 1))} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10" aria-label="Previous month">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{format(calendarMonth, 'MMMM yyyy')}</span>
                <button type="button" onClick={() => setCalendarMonth((month) => addMonths(month, 1))} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10" aria-label="Next month">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-gray-400 dark:text-gray-500">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <span key={day} className="py-1">{day}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-y-0.5">
                {calendarDays.map((day) => {
                  const isSelected = selectedDate && isSameDay(day, selectedDate)
                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => setDateForField(dateField, day)}
                      className={cn(
                        'mx-auto flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors',
                        isSelected ? 'bg-primary text-white shadow-sm' : isToday(day) ? 'text-primary underline decoration-2 underline-offset-4' : isSameMonth(day, calendarMonth) ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10' : 'text-gray-300 hover:bg-gray-50 dark:text-gray-600 dark:hover:bg-white/5',
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  )
                })}
              </div>

              <div className="mt-4 space-y-2 border-t border-gray-200 pt-3.5 dark:border-[#47494e]">
                {(['start', 'due'] as const).map((field) => {
                  const value = field === 'start' ? startDateValue : dueDateValue
                  const fieldDate = getDateFromValue(value)
                  const isEnabled = Boolean(value)
                  return (
                    <div key={field} className={cn('rounded-lg p-2 transition-colors', dateField === field ? 'bg-gray-100 dark:bg-white/8' : '')}>
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          'relative flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-transparent transition-colors duration-150',
                          isEnabled
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-400 text-transparent hover:border-gray-600 dark:border-gray-500 dark:hover:border-gray-300',
                        )}>
                          <input
                            id={`card-${field}-date-enabled`}
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(event) => toggleDateField(field, event.target.checked)}
                            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                            aria-label={`${field === 'start' ? 'Start' : 'Due'} date enabled`}
                          />
                          {isEnabled && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                        </span>
                        <label htmlFor={`card-${field}-date-enabled`} className="w-[68px] text-xs font-semibold text-gray-600 dark:text-gray-300">
                          {field === 'start' ? 'Start date' : 'Due date'}
                        </label>
                        <button type="button" disabled={!isEnabled} onClick={() => setDateField(field)} className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-left text-xs font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-[#33353a] dark:text-gray-200">
                          {fieldDate ? format(fieldDate, 'MMM d, yyyy') : 'M/D/YYYY'}
                        </button>
                        <div ref={timePickerField === field ? timePickerRef : undefined} className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              if (!isEnabled) toggleDateField(field, true)
                              setDateField(field)
                              setTimePickerField(timePickerField === field ? null : field)
                              setCustomTimeValue(fieldDate ? format(fieldDate, 'h:mm a') : '')
                            }}
                            className="w-[90px] rounded-md border border-gray-300 bg-white px-1.5 py-1.5 text-left text-xs font-medium text-gray-700 transition-colors hover:border-primary/50 dark:border-gray-600 dark:bg-[#33353a] dark:text-gray-200"
                          >
                            <span className="flex items-center justify-between gap-1">
                              {fieldDate ? format(fieldDate, 'h:mm a') : 'Time'}
                              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                            </span>
                          </button>
                          {timePickerField === field && (
                            <div className="card-time-menu absolute right-0 z-20 mt-1 w-44 rounded-lg border border-gray-200 bg-white p-1.5 shadow-xl dark:border-gray-600 dark:bg-[#2d2f33]">
                              <div className="max-h-40 overflow-y-auto thin-scroll">
                                {TIME_OPTIONS.map((time) => (
                                  <button key={time.value} type="button" onClick={() => { setTimeForField(field, time.value); setTimePickerField(null) }} className="block w-full rounded px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10">
                                    {time.label}
                                  </button>
                                ))}
                              </div>
                              <div className="mt-1.5 border-t border-gray-200 pt-1.5 dark:border-gray-600">
                                <input autoFocus value={customTimeValue} onChange={(event) => setCustomTimeValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') applyCustomTime(field) }} placeholder="9:30 AM or 21:30" className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
                                <button type="button" onClick={() => applyCustomTime(field)} className="mt-1 w-full rounded bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200">Set custom time</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-4 flex items-center justify-between">
                <button type="button" onClick={handleRemoveDates} className="text-xs font-semibold text-red-500 transition-colors hover:text-red-600">Remove dates</button>
                <div className="flex gap-2">
                  <button type="button" onClick={cancelDatePicker} className="rounded-md px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10">Cancel</button>
                  <button type="button" onClick={handleDateSave} className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-primary/90">Save</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
